import React from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import merge from 'lodash/merge';
import mergeWith from 'lodash/mergeWith';
import find from 'lodash/find';

import { Card, Row, Col, Alert } from 'react-bootstrap-5';

import HorizontalForm from 'components/form/horizontal_form';
import LoadingSpinner from 'components/ui/loading_spinner';
import ResearchPropertySelector from 'components/research_property/research_property_select';
import { createSurveyProperty, updateSurveyProperty } from 'components/assignments/api';
import { fetchProperty } from 'components/research_property/api';
import PhotoForm from 'components/photos/photo_form';
import InlineContactSelector from 'components/contacts/inline_contact_selector';
import ContactRolePropTypes from 'bundles/project/prop_types/contact_roles';
import PropertyInfoSection from './property_info_section';
import BuildingInfoSection from './building_info_section';
import LandInfoSection from './land_info_section';
import LeaseSpaceForm from './lease_space_form';
import { defaultProperty } from './assignment_constants';

const nullMerge = (a, b) => b === null ? a : undefined;

export default class SurveyPropertyForm extends React.Component {
  static propTypes = {
    assignmentPath: PropTypes.string.isRequired,
    availableDatabaseOptions: PropTypes.array.isRequired,
    buildingClasses: PropTypes.array.isRequired,
    contactRoles: ContactRolePropTypes,
    contacts: PropTypes.array,
    createPath: PropTypes.string,
    editPath: PropTypes.string,
    isLeaseSurveyProperty: PropTypes.bool.isRequired,
    leaseRateUnitOptions: PropTypes.array.isRequired,
    leaseTypeOptions: PropTypes.array.isRequired,
    measurementAreaLabel: PropTypes.string.isRequired,
    photoAttachments: PropTypes.array,
    photoTypeOptionsForSelect: PropTypes.arrayOf(PropTypes.string),
    property: PropTypes.object,
    stateOptions: PropTypes.arrayOf(PropTypes.array).isRequired
  }

  state = {
    useExistingProperty: false,
    useExistingLeaseSpace: false,
    property: mergeWith(defaultProperty, this.props.property || {}, nullMerge),
    saving: false,
    contacts: this.props.contacts || [],
    errors: null,
    expandBuildingInfo: false,
    description: get(this.props, 'surveyProperty.description') || '',
    salePrice: get(this.props, 'surveyProperty.salePrice') || '',
    capRate: get(this.props, 'surveyProperty.capRate') || '',
    photoAttachments: this.props.photoAttachments || [],
    leaseDetails: {
      additionalRent: get(this.props, 'leaseDetails.additionalRent') || '',
      cam: get(this.props, 'leaseDetails.cam') ||  '',
      camDescription: get(this.props, 'leaseDetails.camDescription') ||  '',
      escalationPct: get(this.props, 'leaseDetails.escalationPct') || '',
      leaseNote: get(this.props, 'leaseDetails.leaseNote') || '',
      leaseRate: get(this.props, 'leaseDetails.leaseRate') || '',
      leaseRateUnits: get(this.props, 'leaseDetails.leaseRateUnits') || this.props.leaseRateUnitOptions[0][1],
      leaseTermMonths: get(this.props, 'leaseDetails.leaseTermMonths') || '',
      leaseTypeId: get(this.props, 'leaseDetails.leaseTypeId') || '',
      maxContiguousArea: get(this.props, 'leaseDetails.maxContiguousArea') || '',
      minDivisibleArea: get(this.props, 'leaseDetails.minDivisibleArea') || '',
      netEffectiveRate: get(this.props, 'leaseDetails.netEffectiveRate') || '',
      size: get(this.props, 'leaseDetails.size') || '',
      sublease: get(this.props, 'leaseDetails.sublease', false),
      suite: get(this.props, 'leaseDetails.suite') || ''
    }
  }

  handlePhotoChange = (photoAttachments) => {
    this.setState({ photoAttachments });
  }

  handleSave = () => {
    const data = {};
    const leaseData = this.props.isLeaseSurveyProperty ? this.state.leaseDetails : null;

    this.setState({ saving: true });

    data.description = this.state.description;
    data.salePrice = this.state.salePrice;
    data.capRate = this.state.capRate;
    data.id = this.state.id;

    data.researchPropertyAttributes = { ...this.state.property };
    data.contactRolesAttributes = this.state.contacts;
    data.photoAttachmentsAttributes = this.state.photoAttachments;

    if (this.state.useExistingProperty) {
      delete data.researchPropertyAttributes.databaseId;
      data.researchPropertyId = this.state.property.id;
    }

    if (this.props.createPath) {
      createSurveyProperty(this.props.createPath, data, leaseData)
        .then(() => {
          window.location.href = this.props.assignmentPath;
        })
        .catch((errors) => {
          window.scrollTo(0, 0);
          this.setState({ saving: false, errors });
        });
    } else {
      updateSurveyProperty(this.props.editPath, data, leaseData)
        .then(() => {
          window.location.href = this.props.assignmentPath;
        })
        .catch((errors) => {
          window.scrollTo(0, 0);
          this.setState({ saving: false, errors });
        });
    }

  }

  useExistingProperty = (e) => {
    e.preventDefault();
    this.setState({ useExistingProperty: true });
  }

  useNewProperty = (e) => {
    e.preventDefault();
    this.setState((prevState) => {
      const property = { ...prevState.property, id: null };
      return { useExistingProperty: false, property };
    });
  }

  useExistingLeaseSpace = () => {
    this.setState({ useExistingLeaseSpace: true });
  }

  existingPropertyIsSelected = () => {
    return !!this.state.property.id;
  }

  useNewLeaseSpace = () => {
    this.setState({ useExistingLeaseSpace: false });
  }

  handleChange = (e) => {
    const { name } = e.target;
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    this.setState({ [name]: value });
  }

  handlePropertyChange = (e) => {
    const { name } = e.target;
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;

    this.setState((prevState) => {
      const property = { ...prevState.property, [name]: value };
      return { property };
    });
  }

  handlePropertyTypeChange = (event) => {
    const { propertyTypeId, propertySubtypeId } = event;
    this.setState((prevState) => {
      const property = { ...prevState.property, propertyTypeId, propertySubtypeId };
      return { property };
    });
  }

  handleLeaseSpaceChange = (e) => {
    const { name } = e.target;
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;

    this.setState((prevState) => {
      const leaseDetails = { ...prevState.leaseDetails, [name]: value };
      return { leaseDetails };
    });
  }

  propertySelected = (selectedPropertyOption) => {
    fetchProperty(selectedPropertyOption.value, { serialization_level: "rprop_full_details" }).then((property) => {
      const newProperty = { ...defaultProperty };
      mergeWith(newProperty, property, nullMerge);
      const photoAttachments = this.newPhotoAttachmentsFor(newProperty);
      this.setState({ property: newProperty, existingLeaseSpace: null, photoAttachments });
    });
  }

  newPhotoAttachmentsFor = (property) => {
    if (!property.photoIds) return [];
    const photoAttachments = property.photoIds.map((id, index) => {
      // TODO: we should see if we can use the s3 url isntead of the photos controller, may be able to remove the controller as well.
      return { sortOrder: index, photoId: id, copy: true, thumbnailUrl: `/photos/${id}` };
    });
    delete property.photoIds;
    return photoAttachments;
  }

  leaseSpaceSelected = (e) => {
    const leaseSpaceId = e.target.value;
    const leaseSpace = find(this.state.property.leases, leaseSpace => leaseSpace.id === parseInt(leaseSpaceId));

    if (!leaseSpace) return;

    this.setState((prevState) => {
      const leaseDetails = merge(prevState.leaseDetails, leaseSpace);
      return { leaseDetails };
    });
  }

  availableLeaseSpaces() {
    if (!this.state.property.leases) return [];

    return this.state.property.leases;
  }

  isPersisted() {
    return !!get(this.props, 'surveyProperty.id');
  }

  formIsValid() {
    if (this.state.useExistingProperty && !this.existingPropertyIsSelected()) return false;
    if (this.props.isLeaseSurveyProperty && (this.state.useExistingLeaseSpace && !this.state.existingLeaseSpace)) {
      return false;
    }

    return true;
  }

  renderErrors() {
    if (this.state.errors) {
      const errorDivs = this.state.errors.map((error, index) => {
        return <div key={index}>{error}</div>;
      });

      return (
        <Alert className="mb-4" variant="warning">
          {errorDivs}
        </Alert>
      );
    } else {
      return null;
    }
  }

  renderLeaseSpaceSelect() {
    if (this.props.isLeaseSurveyProperty && this.state.useExistingProperty && this.state.property.id) {
      return (
        <React.Fragment>
          <Row className='row-eq-height mt-5'>
            <Col className='clickable' xs={5} onClick={this.useNewLeaseSpace}>
              <Card className={this.state.useExistingLeaseSpace ? '' : 'highlight'}>
                <Card.Body>
                  <h4>Create a lease space</h4>
                  <p>Add a new lease space to this property and use it in your survey.</p>
                </Card.Body>
              </Card>
            </Col>
            <Col className='text-center' xs={2}>OR</Col>
            <Col className='clickable' xs={5} onClick={this.useExistingLeaseSpace}>
              <Card className={this.state.useExistingLeaseSpace ? 'highlight' : ''}>
                <Card.Body>
                  <h4>Select an existing lease space</h4>
                  <p>Add an existing lease space from this property to your survey.</p>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {!this.state.useExistingLeaseSpace && this.renderNewLeaseSpaceForm()}

          {this.state.useExistingLeaseSpace && this.renderExistingLeaseSpaceForm()}
        </React.Fragment>
      );
    } else {
      return null;
    }
  }

  renderBuyDetails() {
    if (this.props.isLeaseSurveyProperty) return null;
    if (this.state.useExistingProperty && !this.existingPropertyIsSelected()) return null;

    return (
      <div>
        <h6 class="mb-2">Purchase Details</h6>
        <HorizontalForm>
          <HorizontalForm.TextInput
            label='Price'
            name='salePrice'
            number
            value={this.state.salePrice}
            onChange={this.handleChange}
          />
          <HorizontalForm.TextInput
            label='Cap Rate'
            name='capRate'
            number
            value={this.state.capRate}
            onChange={this.handleChange}
          />
          <HorizontalForm.TextArea
            label="Survey Description"
            name='description'
            value={this.state.description || ""}
            onChange={this.handleChange}
          />
        </HorizontalForm>
      </div>
    );
  }

  renderMedia() {
    if (this.state.useExistingProperty && !this.existingPropertyIsSelected()) return null;

    return (
      <HorizontalForm name='media-form'>
        <h6 class="mb-2">Media</h6>
        <PhotoForm
          classSlug="tenant_rep_property"
          maxPhotos={100}
          photoAttachments={this.state.photoAttachments}
          photoTypeOptions={this.props.photoTypeOptionsForSelect}
          onPhotoChange={this.handlePhotoChange}
        />
      </HorizontalForm>
    );
  }

  renderNewLeaseSpaceForm() {
    if (!this.props.isLeaseSurveyProperty) {
      return null;
    }

    return (
      <HorizontalForm>
        <h6 class="mb-2">Space Overview</h6>

        {!this.isPersisted() && this.useExistingProperty &&
          this.renderExistingLeaseSpaceForm()
        }
        <LeaseSpaceForm
          description={this.state.description}
          handleChange={this.handleChange}
          handleLeaseSpaceChange={this.handleLeaseSpaceChange}
          leaseDetails={this.state.leaseDetails}
          leaseRateUnitOptions={this.props.leaseRateUnitOptions}
          leaseTypeOptions={this.props.leaseTypeOptions}
          measurementAreaLabel={this.props.measurementAreaLabel}
          stateOptions={this.props.stateOptions}
        />
      </HorizontalForm>
    );
  }

  renderPropertyForm() {
    const { property } = this.state;
    if (this.state.useExistingProperty && !property.id) return null;

    return (
      <React.Fragment>
        <HorizontalForm>
          <div className="d-flex flex-column gap-8">
            {!property.id &&
              <div>
                <h6 className='mb-2'>Database</h6>
                <div className='mb-4'>This property will be saved to the database specified below.</div>
                <HorizontalForm.Select
                  includeBlank='-- select a database --'
                  label='Database'
                  name='databaseId'
                  options={this.props.availableDatabaseOptions}
                  required
                  value={property.databaseId ? property.databaseId.toString() : ""}
                  onChange={this.handlePropertyChange}
                />
              </div>
            }

            <div>{this.renderLocationInformation()}</div>
            <div>{this.renderPropertyInformation()}</div>
            <div>{this.renderBuildingInformation()}</div>
            <div>{this.renderLandInformation()}</div>
          </div>
        </HorizontalForm>
      </React.Fragment>
    );
  }

  renderLocationInformation() {
    return (
      <React.Fragment>
        <h6 className="mb-2">
          { this.state.useExistingProperty ? 'Selected Location Information' : 'Location Information' }
        </h6>
        <HorizontalForm.TextInput
          disabled={this.state.useExistingProperty}
          name='address'
          required
          value={this.state.property.address}
          onChange={this.handlePropertyChange}
        />
        <HorizontalForm.TextInput
          disabled={this.state.useExistingProperty}
          name='city'
          required
          value={this.state.property.city}
          onChange={this.handlePropertyChange}
        />
        <HorizontalForm.Select
          disabled={this.state.useExistingProperty}
          includeBlank
          label="State"
          name="state"
          options={this.props.stateOptions}
          required
          value={this.state.property.state}
          onChange={this.handlePropertyChange}
        />
        <HorizontalForm.TextInput
          disabled={this.state.useExistingProperty}
          name='zip'
          required
          value={this.state.property.zip}
          onChange={this.handlePropertyChange}
        />
        <HorizontalForm.TextInput
          disabled={this.state.useExistingProperty}
          name='county'
          value={this.state.property.county}
          onChange={this.handlePropertyChange}
        />
        <HorizontalForm.TextInput
          name='market'
          value={this.state.property.market}
          onChange={this.handlePropertyChange}
        />
        <HorizontalForm.TextInput
          name='submarket'
          value={this.state.property.submarket}
          onChange={this.handlePropertyChange}
        />
        <HorizontalForm.TextInput
          name='microMarket'
          value={this.state.property.microMarket}
          onChange={this.handlePropertyChange}
        />
        <HorizontalForm.TextInput
          name='crossStreets'
          value={this.state.property.crossStreets}
          onChange={this.handlePropertyChange}
        />
        <HorizontalForm.TextArea
          name='locationDescription'
          value={this.state.property.locationDescription || ""}
          onChange={this.handlePropertyChange}
        />
        <HorizontalForm.TextArea
          name='siteDescription'
          value={this.state.property.siteDescription || ""}
          onChange={this.handlePropertyChange}
        />
      </React.Fragment>
    );
  }

  renderPropertyInformation() {
    return (
      <PropertyInfoSection
        handlePropertyTypeChange={this.handlePropertyTypeChange}
        property={this.state.property}
        onChange={this.handlePropertyChange}
      />
    );
  }

  renderBuildingInformation() {
    return (
      <BuildingInfoSection
        buildingClasses={this.props.buildingClasses}
        property={this.state.property}
        onChange={this.handlePropertyChange}
      />
    );
  }

  renderLandInformation() {
    if (this.state.property.propertyTypeId != 5) return; // Only render if property type is land

    return (
      <LandInfoSection
        property={this.state.property}
        onChange={this.handlePropertyChange}
      />
    );
  }

  renderExistingPropertyForm() {
    return (
      <React.Fragment>
        <HorizontalForm>
          <h6 class="mb-2">Select Existing Property</h6>
          <ResearchPropertySelector onModelAdded={this.propertySelected} />
        </HorizontalForm>
      </React.Fragment>
    );
  }

  renderExistingLeaseSpaceForm() {
    if (!this.state.useExistingProperty || this.availableLeaseSpaces().length === 0) {
      return null;
    } else {
      const leaseOptions = this.availableLeaseSpaces().map((leaseSpace) => {
        return { value: leaseSpace.id, label: leaseSpace.suite, leaseSpace };
      });
      return (
        <div className='bottom-buffer-lg'>
          <div>Prefill with information from an existing lease space.</div>
          <HorizontalForm.Select
            includeBlank='-- select a lease space --'
            name='leaseSpace'
            options={leaseOptions}
            value={null}
            onChange={this.leaseSpaceSelected}
          />
        </div>
      );
    }
  }

  setContacts = (contacts) => {
    this.setState({ contacts });
  }

  renderAddContactForm() {
    return (
      <HorizontalForm>
        <h6 class="mb-2">Location Contacts</h6>
        <div className="fs-large">Add related contacts such as Owner, Investor, Property Manager, etc.</div>
        <InlineContactSelector
          availableDatabaseOptions={this.props.availableDatabaseOptions}
          contactRoles={this.props.contactRoles}
          contacts={this.state.contacts}
          databaseContactRoles={this.state.property.databaseContactRoles}
          onContactsUpdate={this.setContacts}
        />
      </HorizontalForm>
    );
  }

  render() {
    return (
      <Card>
        <Card.Body>
          {this.renderErrors()}

          {this.renderPropertySelect()}

          {this.state.useExistingProperty && this.renderExistingPropertyForm()}

          <div className="d-flex flex-column gap-8">
            {this.renderPropertyForm()}

            {this.renderBuyDetails()}

            {this.renderNewLeaseSpaceForm()}

            <div></div>{this.renderAddContactForm()}

            {this.renderMedia()}

            <div className='mt-5 d-flex justify-content-end gap-2'>
              <a className='btn btn-ghost' href={this.props.assignmentPath}>Cancel</a>
              <button
                className='btn btn-primary'
                disabled={this.state.saving || !this.formIsValid()}
                name='save'
                onClick={this.handleSave}
              >
                {this.state.saving &&
                  <span>
                    <LoadingSpinner className="me-2" inline size="md" />
                    Saving ...
                  </span>
                }
                {!this.state.saving &&
                  <span>Save</span>
                }
              </button>
            </div>
          </div>
        </Card.Body>
      </Card>
    );
  }

  renderPropertySelect() {
    if (this.isPersisted()) return null;

    return (
      <React.Fragment>
        <Card className="border-dashed mb-4">
          <Card.Body className="fs-large fw-light text-center">
            <span>Create a survey location from a </span>
            {this.state.useExistingProperty &&
              <span >database property</span>
            }
            {!this.state.useExistingProperty &&
              <a href="#" name="use-existing" onClick={this.useExistingProperty}>database property</a>
            }
          </Card.Body>
        </Card>
        <h4 className="text-center fs-large fw-light text-center mb-4">
          {this.state.useExistingProperty &&
            <a href="#" name='create-new' onClick={this.useNewProperty}>Or create a new property</a>
          }
          {!this.state.useExistingProperty &&
            <span>Or create a new property below</span>
          }
        </h4>
      </React.Fragment>
    );
  }
}
